import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Avatar,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as TruckIcon,
  RvHookup as TrailerIcon,
  Person as PersonIcon,
  GpsFixed as GpsIcon,
  Build as MaintenanceIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Sync as SyncIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiCardContent-root': {
    padding: theme.spacing(3)
  }
}));

const StatusChip = ({ isActive }) => (
  <Chip
    label={isActive ? "Active" : "Inactive"}
    color={isActive ? "success" : "error"}
    size="small"
  />
);

const TruckStatusChip = ({ isActive, isWinterCapable }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <StatusChip isActive={isActive} />
    {isWinterCapable && (
      <Chip
        label="Winter Capable"
        color="info"
        size="small"
        sx={{ bgcolor: '#e3f2fd' }}
      />
    )}
  </Box>
);

const FleetManagement = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  
  // Trucks state
  const [trucks, setTrucks] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Trailers state
  const [trailers, setTrailers] = useState([]);
  
  // Common state
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [openSyncConfirm, setOpenSyncConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add', 'edit', 'view'
  const [dialogType, setDialogType] = useState('truck'); // 'truck' or 'trailer'
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Truck form data
  const [truckFormData, setTruckFormData] = useState({
    name: '',
    year: '',
    make: '',
    model: '',
    color: '',
    vin: '',
    plate_number: '',
    province: 'ON',
    pmvi_id: '',
    fence_vehicle_name: '',
    fence_vehicle_id: '',
    is_winter_capable: false,
    is_active: true,
    current_driver_user_id: '',
    current_passenger_user_id: '',
    odometer_km: '',
    rgw: '',
    attributes: '',
    image_data: ''
  });

  // Trailer form data
  const [trailerFormData, setTrailerFormData] = useState({
    name: '',
    plate_number: '',
    description: '',
    year: '',
    make: '',
    model: '',
    color: '',
    vin: '',
    province: 'ON',
    inspection_name: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load trucks
      const trucksResponse = await fetch('/api/routes/trucks?include_inactive=true', { headers });
      if (!trucksResponse.ok) throw new Error(t('fleet.errors.loadTrucks'));
      const trucksData = await trucksResponse.json();
      let truckRows = trucksData.trucks || [];

      // Crew Manager and office users see the latest real GPS ping directly
      // on the Trucks page. Missing pings remain visibly unavailable.
      const gpsResponse = await fetch('/api/gps/locations/current?include_inactive=true', { headers });
      if (gpsResponse.ok) {
        const gpsData = await gpsResponse.json();
        const gpsByTruck = new Map(
          (gpsData.truck_locations || []).map((location) => [String(location.truck_id), location])
        );
        truckRows = truckRows.map((truck) => ({
          ...truck,
          ...(gpsByTruck.get(String(truck.id)) || {})
        }));
      }
      setTrucks(truckRows);

      // Load trailers
      const trailersResponse = await fetch('/api/trailers?include_inactive=true', { headers });
      if (!trailersResponse.ok) throw new Error(t('fleet.errors.loadTrailers'));
      const trailersData = await trailersResponse.json();
      setTrailers(trailersData.trailers || []);

      // Load employees for driver assignment
      const employeesResponse = await fetch('/api/routes/lookups/available-employees', { headers });
      if (employeesResponse.ok) {
        const employeesData = await employeesResponse.json();
        setUsers(employeesData.available_employees || []);
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== TRUCK HANDLERS ====================
  const handleOpenTruckDialog = (mode, truck = null) => {
    setDialogMode(mode);
    setDialogType('truck');
    setSelectedItem(truck);
    if (truck) {
      setTruckFormData({
        name: truck.name || '',
        year: truck.year || '',
        make: truck.make || '',
        model: truck.model || '',
        color: truck.color || '',
        vin: truck.vin || '',
        plate_number: truck.plate_number || '',
        province: truck.province || 'ON',
        pmvi_id: truck.pmvi_id || '',
        fence_vehicle_name: truck.fence_vehicle_name || '',
        fence_vehicle_id: truck.fence_vehicle_id || '',
        is_winter_capable: truck.is_winter_capable || false,
        is_active: truck.is_active !== false,
        current_driver_user_id: truck.current_driver_user_id || '',
        current_passenger_user_id: truck.current_passenger_user_id || '',
        odometer_km: truck.odometer_km || '',
        rgw: truck.rgw || '',
        attributes: truck.attributes || '',
        image_data: truck.image_data || ''
      });
    } else {
      setTruckFormData({
        name: '',
        year: '',
        make: '',
        model: '',
        color: '',
        vin: '',
        plate_number: '',
        province: 'ON',
        pmvi_id: '',
        fence_vehicle_name: '',
        fence_vehicle_id: '',
        is_winter_capable: false,
        is_active: true,
        current_driver_user_id: '',
        current_passenger_user_id: '',
        odometer_km: '',
        rgw: '',
        attributes: '',
        image_data: ''
      });
    }
    setOpenDialog(true);
  };

  const handleSubmitTruck = async () => {
    if (!truckFormData.name?.trim()) {
      setError(t('fleet.errors.vehicleNameRequired'));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        ...truckFormData,
        current_driver_user_id: truckFormData.current_driver_user_id || null,
        current_passenger_user_id: truckFormData.current_passenger_user_id || null,
        odometer_km: truckFormData.odometer_km ? parseInt(truckFormData.odometer_km) : null
      };

      let response;
      if (dialogMode === 'add') {
        response = await fetch('/api/routes/trucks', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
      } else if (dialogMode === 'edit') {
        response = await fetch(`/api/routes/trucks/${selectedItem.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.saveFailed'));
      }

      setSuccess(dialogMode === 'add' ? t('fleet.success.vehicleAdded') : t('fleet.success.vehicleUpdated'));
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTruck = async () => {
    setOpenDeleteConfirm(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/routes/trucks/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...selectedItem,
          is_active: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.deactivateFailed'));
      }

      setSuccess(t('fleet.success.vehicleDeactivated'));
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setSelectedItem(null);
    }
  };

  // ==================== TRAILER HANDLERS ====================
  const handleOpenTrailerDialog = (mode, trailer = null) => {
    setDialogMode(mode);
    setDialogType('trailer');
    setSelectedItem(trailer);
    if (trailer) {
      setTrailerFormData({
        name: trailer.name || '',
        plate_number: trailer.plate_number || '',
        description: trailer.description || '',
        year: trailer.year || '',
        make: trailer.make || '',
        model: trailer.model || '',
        color: trailer.color || '',
        vin: trailer.vin || '',
        province: trailer.province || 'ON',
        inspection_name: trailer.inspection_name || '',
        is_active: trailer.is_active !== false
      });
    } else {
      setTrailerFormData({
        name: '',
        plate_number: '',
        description: '',
        year: '',
        make: '',
        model: '',
        color: '',
        vin: '',
        province: 'ON',
        inspection_name: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleSubmitTrailer = async () => {
    if (!trailerFormData.name?.trim()) {
      setError(t('fleet.errors.trailerNameRequired'));
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Auto-generate inspection_name if not provided
      const payload = {
        ...trailerFormData,
        inspection_name: trailerFormData.inspection_name || 
          `${trailerFormData.plate_number || ''} - ${trailerFormData.name}`.trim()
      };

      let response;
      if (dialogMode === 'add') {
        response = await fetch('/api/trailers', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
      } else if (dialogMode === 'edit') {
        response = await fetch(`/api/trailers/${selectedItem.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.saveFailed'));
      }

      setSuccess(dialogMode === 'add' ? t('fleet.success.trailerAdded') : t('fleet.success.trailerUpdated'));
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrailer = async () => {
    setOpenDeleteConfirm(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/trailers/${selectedItem.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.deactivateFailed'));
      }

      setSuccess(t('fleet.success.trailerDeactivated'));
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setSelectedItem(null);
    }
  };

  const handleReactivateTrailer = async (trailer) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/trailers/${trailer.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.reactivateFailed'));
      }

      setSuccess(t('fleet.success.trailerReactivated'));
      loadData();
    } catch (err) {
      console.error('Reactivate error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== COMMON HANDLERS ====================
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setError('');
  };

  const handleTruckInputChange = (field, value) => {
    setTruckFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTrailerInputChange = (field, value) => {
    setTrailerFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSyncFleet = async () => {
    setOpenSyncConfirm(false);
    setSyncing(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/gps/sync-fleet', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('fleet.errors.syncFailed'));
      }

      const data = await response.json();
      setSuccess(
        t('fleet.success.syncCompleted', { 
          created: data.summary.created, 
          updated: data.summary.updated, 
          skipped: data.summary.skipped 
        })
      );
      
      loadData();
    } catch (err) {
      console.error('Sync fleet error:', err);
      setError(err.message || t('fleet.errors.syncFailed'));
    } finally {
      setSyncing(false);
    }
  };

  const formatDriverInfo = (truck) => {
    const parts = [];
    if (truck.driver_first_name) {
      parts.push(`${t('fleet.driver')}: ${truck.driver_first_name} ${truck.driver_last_name || ''}`);
    }
    if (truck.passenger_first_name) {
      parts.push(`${t('fleet.passenger')}: ${truck.passenger_first_name} ${truck.passenger_last_name || ''}`);
    }
    return parts.length > 0 ? parts.join(', ') : t('fleet.noAssignments');
  };

  const openDeleteConfirmDialog = (item, type) => {
    setSelectedItem(item);
    setDialogType(type);
    setOpenDeleteConfirm(true);
  };

  // ==================== RENDER ====================
  return (
    <Box sx={{ width: '100%', maxWidth: 1400, margin: '0 auto', p: 3 }} data-demo-target="page-fleet-root">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, color: '#1976d2', fontWeight: 'bold' }}>
            🚛 {t('fleet.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            {t('fleet.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeTab === 0 && !readOnly && (
            <>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={() => setOpenSyncConfirm(true)}
                disabled={syncing}
                aria-label={t('fleet.syncFromGps')}
              >
                {syncing ? t('fleet.syncing') : t('fleet.syncFromGps')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenTruckDialog('add')}
                aria-label={t('fleet.addVehicle')}
              >
                {t('fleet.addVehicle')}
              </Button>
            </>
          )}
          {activeTab === 1 && !readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenTrailerDialog('add')}
              aria-label={t('fleet.addTrailer')}
            >
              {t('fleet.addTrailer')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label={t('fleet.tabs.ariaLabel')}
        >
          <Tab 
            icon={<TruckIcon />} 
            iconPosition="start" 
            label={`${t('fleet.tabs.vehicles')} (${trucks.length})`}
            aria-label={t('fleet.tabs.vehicles')}
          />
          <Tab 
            icon={<TrailerIcon />} 
            iconPosition="start" 
            label={`${t('fleet.tabs.trailers')} (${trailers.length})`}
            aria-label={t('fleet.tabs.trailers')}
          />
        </Tabs>
      </Paper>

      {loading && !openDialog && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Vehicles Tab */}
      {activeTab === 0 && (
        <StyledCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TruckIcon color="primary" />
              {t('fleet.vehicleOverview', { count: trucks.length })}
            </Typography>
            
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.vehicle')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.details')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.status')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.assignments')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.gpsInfo')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('fleet.columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trucks.map((truck, idx) => (
                    <TableRow key={truck.id} hover data-demo-target={idx === 0 ? 'page-fleet-row-first' : undefined}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#1976d2' }}>
                            <TruckIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                              {truck.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {truck.plate_number || t('fleet.noPlate')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {truck.year && `${truck.year} `}
                          {truck.make && `${truck.make} `}
                          {truck.model && `${truck.model}`}
                        </Typography>
                        {truck.color && (
                          <Typography variant="caption" color="text.secondary">
                            {t('fleet.color')}: {truck.color}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TruckStatusChip 
                          isActive={truck.is_active} 
                          isWinterCapable={truck.is_winter_capable}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {formatDriverInfo(truck)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {truck.today_active_jobs > 0 && t('fleet.activeJobsToday', { count: truck.today_active_jobs })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {truck.fence_vehicle_name ? (
                          <Tooltip title={`${t('fleet.gpsDevice')}: ${truck.fence_vehicle_id || 'N/A'}`}>
                            <Chip
                              icon={<GpsIcon />}
                              label={t('fleet.gpsEnabled')}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            label={t('fleet.noGps')}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {truck.latitude != null && truck.longitude != null ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {t('fleet.liveCoordinates', {
                              latitude: Number(truck.latitude).toFixed(5),
                              longitude: Number(truck.longitude).toFixed(5)
                            })}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {t('fleet.awaitingGps')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={t('fleet.viewDetails')}>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleOpenTruckDialog('view', truck)}
                              aria-label={t('fleet.viewDetails')}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {!readOnly && (
                            <>
                              <Tooltip title={t('fleet.editVehicle')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenTruckDialog('edit', truck)}
                                  aria-label={t('fleet.editVehicle')}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              {truck.is_active && (
                                <Tooltip title={t('fleet.deactivateVehicle')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => openDeleteConfirmDialog(truck, 'truck')}
                                    aria-label={t('fleet.deactivateVehicle')}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {trucks.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t('fleet.noVehicles')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      )}

      {/* Trailers Tab */}
      {activeTab === 1 && (
        <StyledCard>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrailerIcon color="primary" />
              {t('fleet.trailerOverview', { count: trailers.length })}
            </Typography>
            
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.trailer')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.plate')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.details')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.description')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('fleet.columns.status')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('fleet.columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trailers.map((trailer) => (
                    <TableRow key={trailer.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#ff9800' }}>
                            <TrailerIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                              {trailer.name}
                            </Typography>
                            {trailer.inspection_name && trailer.inspection_name !== trailer.name && (
                              <Typography variant="caption" color="text.secondary">
                                {trailer.inspection_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {trailer.plate_number || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {trailer.year && `${trailer.year} `}
                          {trailer.make && `${trailer.make} `}
                          {trailer.model && `${trailer.model}`}
                        </Typography>
                        {trailer.color && (
                          <Typography variant="caption" color="text.secondary">
                            {t('fleet.color')}: {trailer.color}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {trailer.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip isActive={trailer.is_active} />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={t('fleet.viewDetails')}>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleOpenTrailerDialog('view', trailer)}
                              aria-label={t('fleet.viewDetails')}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {!readOnly && (
                            <>
                              <Tooltip title={t('fleet.editTrailer')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenTrailerDialog('edit', trailer)}
                                  aria-label={t('fleet.editTrailer')}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              {trailer.is_active ? (
                                <Tooltip title={t('fleet.deactivateTrailer')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => openDeleteConfirmDialog(trailer, 'trailer')}
                                    aria-label={t('fleet.deactivateTrailer')}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title={t('fleet.reactivateTrailer')}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleReactivateTrailer(trailer)}
                                    aria-label={t('fleet.reactivateTrailer')}
                                  >
                                    <RestoreIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {trailers.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t('fleet.noTrailers')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      )}

      {/* Truck Add/Edit Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'truck'} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { minHeight: '600px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {dialogMode === 'add' ? t('fleet.dialog.addVehicle') : 
             dialogMode === 'edit' ? t('fleet.dialog.editVehicle') : t('fleet.dialog.vehicleDetails')}
          </Typography>
          <IconButton onClick={handleCloseDialog} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                {t('fleet.dialog.basicInfo')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.vehicleName')}
                value={truckFormData.name}
                onChange={(e) => handleTruckInputChange('name', e.target.value)}
                disabled={dialogMode === 'view'}
                error={!truckFormData.name?.trim()}
                helperText={!truckFormData.name?.trim() ? t('fleet.errors.vehicleNameRequired') : ''}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.year')}
                value={truckFormData.year}
                onChange={(e) => handleTruckInputChange('year', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.make')}
                value={truckFormData.make}
                onChange={(e) => handleTruckInputChange('make', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.model')}
                value={truckFormData.model}
                onChange={(e) => handleTruckInputChange('model', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.color')}
                value={truckFormData.color}
                onChange={(e) => handleTruckInputChange('color', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.vin')}
                value={truckFormData.vin}
                onChange={(e) => handleTruckInputChange('vin', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Registration Information */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                {t('fleet.dialog.registrationInfo')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.licensePlate')}
                value={truckFormData.plate_number}
                onChange={(e) => handleTruckInputChange('plate_number', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('fleet.dialog.province')}</InputLabel>
                <Select
                  value={truckFormData.province}
                  onChange={(e) => handleTruckInputChange('province', e.target.value)}
                  disabled={dialogMode === 'view'}
                  label={t('fleet.dialog.province')}
                >
                  <MenuItem value="ON">Ontario</MenuItem>
                  <MenuItem value="BC">British Columbia</MenuItem>
                  <MenuItem value="AB">Alberta</MenuItem>
                  <MenuItem value="MB">Manitoba</MenuItem>
                  <MenuItem value="SK">Saskatchewan</MenuItem>
                  <MenuItem value="QC">Quebec</MenuItem>
                  <MenuItem value="NB">New Brunswick</MenuItem>
                  <MenuItem value="NS">Nova Scotia</MenuItem>
                  <MenuItem value="PE">Prince Edward Island</MenuItem>
                  <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                  <MenuItem value="NT">Northwest Territories</MenuItem>
                  <MenuItem value="YT">Yukon Territory</MenuItem>
                  <MenuItem value="NU">Nunavut</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.pmviId')}
                value={truckFormData.pmvi_id}
                onChange={(e) => handleTruckInputChange('pmvi_id', e.target.value)}
                disabled={dialogMode === 'view'}
                helperText={t('fleet.dialog.pmviIdHelp')}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.odometer')}
                type="number"
                value={truckFormData.odometer_km}
                onChange={(e) => handleTruckInputChange('odometer_km', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* GPS & Tracking */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                {t('fleet.dialog.gpsTracking')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.gpsVehicleName')}
                value={truckFormData.fence_vehicle_name}
                onChange={(e) => handleTruckInputChange('fence_vehicle_name', e.target.value)}
                disabled={dialogMode === 'view'}
                helperText={t('fleet.dialog.gpsVehicleNameHelp')}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.gpsDeviceId')}
                value={truckFormData.fence_vehicle_id}
                onChange={(e) => handleTruckInputChange('fence_vehicle_id', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Driver Assignments */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                {t('fleet.dialog.driverAssignments')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('fleet.dialog.primaryDriver')}</InputLabel>
                <Select
                  value={truckFormData.current_driver_user_id}
                  onChange={(e) => handleTruckInputChange('current_driver_user_id', e.target.value)}
                  disabled={dialogMode === 'view'}
                  label={t('fleet.dialog.primaryDriver')}
                >
                  <MenuItem value="">{t('fleet.dialog.noAssignment')}</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.employee_name || `${user.first_name} ${user.last_name}`} ({user.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('fleet.dialog.passenger')}</InputLabel>
                <Select
                  value={truckFormData.current_passenger_user_id}
                  onChange={(e) => handleTruckInputChange('current_passenger_user_id', e.target.value)}
                  disabled={dialogMode === 'view'}
                  label={t('fleet.dialog.passenger')}
                >
                  <MenuItem value="">{t('fleet.dialog.noAssignment')}</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.employee_name || `${user.first_name} ${user.last_name}`} ({user.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Status & Capabilities */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                {t('fleet.dialog.statusCapabilities')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={truckFormData.is_active}
                    onChange={(e) => handleTruckInputChange('is_active', e.target.checked)}
                    disabled={dialogMode === 'view'}
                  />
                }
                label={t('fleet.dialog.activeVehicle')}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={truckFormData.is_winter_capable}
                    onChange={(e) => handleTruckInputChange('is_winter_capable', e.target.checked)}
                    disabled={dialogMode === 'view'}
                  />
                }
                label={t('fleet.dialog.winterCapable')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {dialogMode === 'view' ? t('common.close') : t('common.cancel')}
          </Button>
          {dialogMode !== 'view' && (
            <Button 
              onClick={handleSubmitTruck} 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : (dialogMode === 'add' ? t('fleet.dialog.addVehicle') : t('common.saveChanges'))}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Trailer Add/Edit Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'trailer'} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {dialogMode === 'add' ? t('fleet.dialog.addTrailer') : 
             dialogMode === 'edit' ? t('fleet.dialog.editTrailer') : t('fleet.dialog.trailerDetails')}
          </Typography>
          <IconButton onClick={handleCloseDialog} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                {t('fleet.dialog.basicInfo')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.trailerName')}
                value={trailerFormData.name}
                onChange={(e) => handleTrailerInputChange('name', e.target.value)}
                disabled={dialogMode === 'view'}
                error={!trailerFormData.name?.trim()}
                helperText={!trailerFormData.name?.trim() ? t('fleet.errors.trailerNameRequired') : ''}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.licensePlate')}
                value={trailerFormData.plate_number}
                onChange={(e) => handleTrailerInputChange('plate_number', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('fleet.dialog.description')}
                value={trailerFormData.description}
                onChange={(e) => handleTrailerInputChange('description', e.target.value)}
                disabled={dialogMode === 'view'}
                multiline
                rows={2}
                helperText={t('fleet.dialog.descriptionHelp')}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label={t('fleet.dialog.inspectionName')}
                value={trailerFormData.inspection_name}
                onChange={(e) => handleTrailerInputChange('inspection_name', e.target.value)}
                disabled={dialogMode === 'view'}
                helperText={t('fleet.dialog.inspectionNameHelp')}
              />
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Vehicle Details */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                {t('fleet.dialog.vehicleDetails')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.year')}
                value={trailerFormData.year}
                onChange={(e) => handleTrailerInputChange('year', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.make')}
                value={trailerFormData.make}
                onChange={(e) => handleTrailerInputChange('make', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.model')}
                value={trailerFormData.model}
                onChange={(e) => handleTrailerInputChange('model', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.color')}
                value={trailerFormData.color}
                onChange={(e) => handleTrailerInputChange('color', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('fleet.dialog.vin')}
                value={trailerFormData.vin}
                onChange={(e) => handleTrailerInputChange('vin', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('fleet.dialog.province')}</InputLabel>
                <Select
                  value={trailerFormData.province}
                  onChange={(e) => handleTrailerInputChange('province', e.target.value)}
                  disabled={dialogMode === 'view'}
                  label={t('fleet.dialog.province')}
                >
                  <MenuItem value="ON">Ontario</MenuItem>
                  <MenuItem value="BC">British Columbia</MenuItem>
                  <MenuItem value="AB">Alberta</MenuItem>
                  <MenuItem value="MB">Manitoba</MenuItem>
                  <MenuItem value="SK">Saskatchewan</MenuItem>
                  <MenuItem value="QC">Quebec</MenuItem>
                  <MenuItem value="NB">New Brunswick</MenuItem>
                  <MenuItem value="NS">Nova Scotia</MenuItem>
                  <MenuItem value="PE">Prince Edward Island</MenuItem>
                  <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                  <MenuItem value="NT">Northwest Territories</MenuItem>
                  <MenuItem value="YT">Yukon Territory</MenuItem>
                  <MenuItem value="NU">Nunavut</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Status */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                {t('fleet.dialog.status')}
              </Typography>
            </Grid>
            
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={trailerFormData.is_active}
                    onChange={(e) => handleTrailerInputChange('is_active', e.target.checked)}
                    disabled={dialogMode === 'view'}
                  />
                }
                label={t('fleet.dialog.activeTrailer')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {dialogMode === 'view' ? t('common.close') : t('common.cancel')}
          </Button>
          {dialogMode !== 'view' && (
            <Button 
              onClick={handleSubmitTrailer} 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={loading}
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            >
              {loading ? <CircularProgress size={20} /> : (dialogMode === 'add' ? t('fleet.dialog.addTrailer') : t('common.saveChanges'))}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6">
              {dialogType === 'truck' ? t('fleet.confirmDeactivateVehicle') : t('fleet.confirmDeactivateTrailer')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {dialogType === 'truck' 
              ? t('fleet.confirmDeactivateVehicleMessage', { name: selectedItem?.name })
              : t('fleet.confirmDeactivateTrailerMessage', { name: selectedItem?.name })}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('fleet.deactivateInfo')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteConfirm(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={dialogType === 'truck' ? handleDeleteTruck : handleDeleteTrailer}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {t('fleet.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Fleet Confirmation Dialog */}
      <Dialog
        open={openSyncConfirm}
        onClose={() => setOpenSyncConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color="primary" />
            <Typography variant="h6">{t('fleet.syncDialog.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('fleet.syncDialog.description')}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>{t('fleet.syncDialog.create')}</strong> {t('fleet.syncDialog.createDesc')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>{t('fleet.syncDialog.update')}</strong> {t('fleet.syncDialog.updateDesc')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('fleet.syncDialog.cacheInfo')}
              </Typography>
            </li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('fleet.syncDialog.safetyInfo')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenSyncConfirm(false)}
            disabled={syncing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSyncFleet}
            variant="contained"
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
          >
            {syncing ? t('fleet.syncing') : t('fleet.syncFleet')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FleetManagement;
